// ИИ → Чат: вопросы о своих данных. ИИ читает через те же инструменты, что и MCP, и ничего не пишет.
// Ошибка провайдера показана текстом, вопрос остаётся в поле, «Повторить» отправляет его снова.

import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Button, Card, EmptyState, ErrorText, IconButton, PageSpinner, Select, Spinner, controlClass } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatDateTime } from '@/lib/format'

import { aiApi, aiKeys, useAiStatus, useConversation, useConversations, type ChatMessage } from './api'
import { Disclaimer, Markdown, NotConfigured } from './parts'

const EXAMPLES = ['Как я спал на этой неделе?', 'Какой у меня рекорд в жиме лёжа?', 'Сколько тренировок было в этом месяце?']

function MessageBubble({ message }: { message: Pick<ChatMessage, 'role' | 'content' | 'tool_calls'> }) {
  const mine = message.role === 'user'
  const used = message.tool_calls.filter((c) => c.status === 'ok').length
  return (
    <div className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-xl px-3 py-2',
          mine ? 'bg-accent-soft text-fg' : 'border border-border bg-surface',
        )}
      >
        {mine ? (
          <p className="whitespace-pre-wrap text-sm">{message.content}</p>
        ) : (
          <Markdown text={message.content ?? ''} />
        )}
        {!mine && used > 0 ? (
          <p className="mt-1 text-xs text-muted" title={message.tool_calls.map((c) => c.name).join(', ')}>
            Смотрел данные: {used} {used === 1 ? 'запрос' : used < 5 ? 'запроса' : 'запросов'}
          </p>
        ) : null}
      </div>
    </div>
  )
}

export function ChatPage() {
  const status = useAiStatus()
  const conversations = useConversations()
  const [params, setParams] = useSearchParams()
  const activeId = params.get('c')
  const conversation = useConversation(activeId)
  const client = useQueryClient()

  const [text, setText] = useState('')
  const [pending, setPending] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [lastSent, setLastSent] = useState<string | null>(null)
  const bottom = useRef<HTMLDivElement>(null)

  const configured = status.data?.configured === true
  const messages = conversation.data?.messages ?? []
  const lastMessage = messages.at(-1)
  // Вопрос уже сохранён в переписке, а ответа нет (сбой провайдера) — его не рисуем второй раз.
  const showPending = pending !== null && !(lastMessage?.role === 'user' && lastMessage.content === pending)

  useEffect(() => {
    bottom.current?.scrollIntoView?.({ block: 'end' })
  }, [messages.length, pending])

  const open = (id: string | null) => {
    setError(null)
    setPending(null)
    setParams(id ? { c: id } : {}, { replace: false })
  }

  const send = async (question: string) => {
    const q = question.trim()
    if (!q || pending !== null) return
    setError(null)
    setPending(q)
    setLastSent(q)
    let id = activeId
    try {
      if (!id) {
        const created = await aiApi.createConversation()
        id = created.id
        setParams({ c: id }, { replace: true })
      }
      await aiApi.send(id, q)
      setText((current) => (current.trim() === q ? '' : current))
    } catch (err) {
      setError(err)
    } finally {
      setPending(null)
      if (id) await client.invalidateQueries({ queryKey: aiKeys.conversation(id) })
      void client.invalidateQueries({ queryKey: aiKeys.conversations })
      void client.invalidateQueries({ queryKey: aiKeys.usage })
    }
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void send(text)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      void send(text)
    }
  }

  const remove = async (id: string) => {
    await aiApi.deleteConversation(id)
    if (id === activeId) open(null)
    void client.invalidateQueries({ queryKey: aiKeys.conversations })
  }

  const list = conversations.data ?? []

  return (
    <div className="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)]">
      <aside className="hidden lg:block">
        <Card
          title="Переписки"
          flush
          actions={
            <Button size="sm" variant="ghost" icon="plus" onClick={() => open(null)}>
              Новая
            </Button>
          }
        >
          {conversations.isPending ? (
            <div className="p-4">
              <Spinner />
            </div>
          ) : list.length === 0 ? (
            <p className="px-4 pb-4 text-sm text-muted">Пока пусто.</p>
          ) : (
            <ul className="pb-2">
              {list.map((c) => (
                <li key={c.id} className="group flex items-center">
                  <button
                    type="button"
                    onClick={() => open(c.id)}
                    className={cn(
                      'min-w-0 flex-1 truncate px-4 py-2 text-left text-sm',
                      c.id === activeId ? 'font-medium text-accent' : 'text-fg hover:bg-surface-2',
                    )}
                  >
                    {c.title || 'Без названия'}
                  </button>
                  <IconButton icon="close" label="Удалить переписку" className="mr-1 opacity-60" onClick={() => void remove(c.id)} />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </aside>

      <div className="grid min-w-0 gap-3">
        <div className="flex items-end gap-2 lg:hidden">
          <Select
            label="Переписка"
            containerClassName="min-w-0 flex-1"
            value={activeId ?? ''}
            onChange={(e) => open(e.target.value || null)}
            options={[{ value: '', label: 'Новая переписка' }, ...list.map((c) => ({ value: c.id, label: c.title || 'Без названия' }))]}
          />
        </div>

        {status.data && !configured ? (
          <Card>
            <NotConfigured />
          </Card>
        ) : null}

        <Card>
          <div className="grid max-h-[60vh] min-h-48 gap-3 overflow-y-auto">
            {activeId && conversation.isPending ? (
              <PageSpinner />
            ) : messages.length === 0 && !showPending ? (
              <EmptyState
                icon="ai"
                title="Спроси о своих данных"
                description="ИИ смотрит сон, вес, тренировки, план, цели и анализы — и отвечает. В чате он ничего не записывает."
                action={
                  <div className="flex flex-wrap justify-center gap-2">
                    {EXAMPLES.map((q) => (
                      <Button key={q} size="sm" disabled={!configured} onClick={() => setText(q)}>
                        {q}
                      </Button>
                    ))}
                  </div>
                }
              />
            ) : (
              <>
                {messages.map((m) => (
                  <MessageBubble key={m.id} message={m} />
                ))}
                {showPending ? <MessageBubble message={{ role: 'user', content: pending, tool_calls: [] }} /> : null}
              </>
            )}
            {pending !== null ? (
              <div className="flex items-center gap-2 text-sm text-muted">
                <Spinner size={16} /> ИИ думает…
              </div>
            ) : null}
            <div ref={bottom} />
          </div>
        </Card>

        {error ? (
          <div className="flex flex-wrap items-center gap-3">
            <ErrorText error={error} />
            <Button size="sm" icon="refresh" onClick={() => void send(text.trim() || lastSent || '')}>
              Повторить
            </Button>
          </div>
        ) : null}

        {/* На телефоне поле ввода прилипает к низу над меню и плашкой тренировки. */}
        <form
          onSubmit={submit}
          className="sticky bottom-(--bottom-ui) z-20 -mx-4 grid gap-2 border-t border-border bg-bg/95 px-4 pb-2 pt-2 backdrop-blur lg:static lg:mx-0 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none"
        >
          <label htmlFor="ai-question" className="sr-only">
            Вопрос
          </label>
          <textarea
            id="ai-question"
            rows={2}
            maxLength={4000}
            value={text}
            disabled={!configured}
            placeholder={configured ? 'Например: как менялся мой вес за месяц?' : 'ИИ не настроен'}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            className={cn(controlClass, 'h-auto min-h-16 resize-y py-2 lg:min-h-20')}
          />
          <div className="flex items-center justify-between gap-3">
            <Disclaimer />
            <Button type="submit" variant="primary" loading={pending !== null} disabled={!configured || !text.trim()}>
              Отправить
            </Button>
          </div>
        </form>
        {conversation.data ? (
          <p className="text-xs text-muted">Начата {formatDateTime(conversation.data.created_at)}</p>
        ) : null}
      </div>
    </div>
  )
}
